<script setup lang="ts">
  import { computed, onBeforeUnmount, ref, useId } from 'vue'
  import type { RouteLocationRaw } from 'vue-router'

  import BfButton from './BfButton.vue'
  import BfIconCheck from '@/components/ui/icons/BfIconCheck.vue'
  import BfIconCopy from '@/components/ui/icons/BfIconCopy.vue'

  type Tone = 'success' | 'pending' | 'information'

  const props = withDefaults(
    defineProps<{
      title: string
      tone?: Tone
      referenceLabel?: string
      reference?: string
      referenceTo?: RouteLocationRaw | null
      copyable?: boolean
      copyLabel?: string
    }>(),
    {
      tone: 'success',
      referenceLabel: '',
      reference: '',
      referenceTo: null,
      copyable: false,
      copyLabel: 'Copy reference',
    },
  )

  const titleId = useId()
  const copied = ref(false)
  let copiedTimer: ReturnType<typeof setTimeout> | undefined

  const hasReference = computed(() => Boolean(props.reference))

  const copy = async (): Promise<void> => {
    await navigator.clipboard.writeText(props.reference)
    copied.value = true
    clearTimeout(copiedTimer)
    copiedTimer = setTimeout(() => (copied.value = false), 1500)
  }

  onBeforeUnmount(() => clearTimeout(copiedTimer))
</script>

<template>
  <section
    class="confirmation-panel"
    :class="tone === 'success' ? null : `confirmation-panel--${tone}`"
    :aria-labelledby="titleId"
  >
    <div class="confirmation-panel__mark" aria-hidden="true">
      <slot name="mark">
        <BfIconCheck class="confirmation-panel__mark-icon" :size="32" />
      </slot>
    </div>

    <h1 :id="titleId" class="confirmation-panel__title">{{ title }}</h1>

    <p v-if="$slots.lead" class="confirmation-panel__lead"><slot name="lead" /></p>

    <p v-if="hasReference" class="confirmation-panel__reference">
      <span v-if="referenceLabel" class="confirmation-panel__reference-label">{{ referenceLabel }}</span>
      <component
        :is="referenceTo ? 'RouterLink' : 'span'"
        class="confirmation-panel__reference-value"
        :to="referenceTo ?? undefined"
      >
        {{ reference }}
      </component>
      <BfButton v-if="copyable" variant="ghost" size="small" :aria-label="copyLabel" @click="copy">
        <template #icon>
          <BfIconCheck v-if="copied" :size="14" />
          <BfIconCopy v-else :size="14" />
        </template>
      </BfButton>
    </p>

    <div v-if="$slots.actions" class="confirmation-panel__actions"><slot name="actions" /></div>

    <div v-if="$slots.footer" class="confirmation-panel__footer"><slot name="footer" /></div>
  </section>
</template>

<style scoped lang="scss">
  .confirmation-panel {
    --confirmation-panel-measure: 560px;
    --confirmation-panel-mark-size: 64px;
    --confirmation-panel-accent: var(--bf-green);
    --confirmation-panel-accent-soft: var(--bf-green-light);
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    width: 100%;
    max-width: var(--confirmation-panel-measure);
    margin-inline: auto;
    padding-block: var(--bf-sp-4) var(--bf-sp-5);
    /* The lead, the reference label and the footnote are body copy. The card inherits this from
       the kit page it is drawn on; the app's <body> is still the legacy Work Sans, so the block
       states it — the title and the reference value name the display face themselves. */
    font-family: var(--bf-font-body);
    isolation: isolate;

    &--information {
      --confirmation-panel-accent: var(--bf-info-accent);
      --confirmation-panel-accent-soft: var(--bf-info-bg);
    }

    &--pending {
      --confirmation-panel-accent: var(--bf-orange-dark);
      --confirmation-panel-accent-soft: var(--bf-orange-ultralight);
    }

    &__mark {
      position: relative;
      display: grid;
      place-items: center;
      width: var(--confirmation-panel-mark-size);
      height: var(--confirmation-panel-mark-size);
      margin-bottom: var(--bf-sp-4);
      border-radius: var(--bf-r-pill);
      background: var(--confirmation-panel-accent);
      color: var(--bf-fg-inverse-1);
      animation: confirmation-panel-mark-in var(--bf-dur-base) cubic-bezier(0.2, 0.8, 0.2, 1) both;

      /* The one-shot ring. */
      &::after {
        content: '';
        position: absolute;
        inset: 0;
        border-radius: inherit;
        background: var(--confirmation-panel-accent-soft);
        z-index: -1;
        animation: confirmation-panel-ring 1.4s cubic-bezier(0.2, 0.8, 0.2, 1) 0.15s forwards;
      }
    }

    &__mark-icon {
      width: 32px;
      height: 32px;
    }

    &__title {
      margin: 0 0 var(--bf-sp-2);
      font-family: var(--bf-font-display);
      font-size: var(--bf-fs-2xl);
      font-weight: var(--bf-fw-bold);
      line-height: var(--bf-lh-tight);
      letter-spacing: var(--bf-tr-tight);
      color: var(--bf-fg-1);
    }

    &__lead {
      margin: 0;
      max-width: 48ch;
      font-size: var(--bf-fs-base);
      line-height: var(--bf-lh-relaxed);
      color: var(--bf-fg-2);

      :deep(strong) {
        color: var(--bf-fg-1);
        font-weight: var(--bf-fw-semibold);
      }
    }

    /* A pill, so the reference reads as a thing to keep rather than a sentence. */
    &__reference {
      display: inline-flex;
      align-items: center;
      gap: var(--bf-sp-2);
      margin-top: var(--bf-sp-4);
      padding: 10px 14px;
      border: 1px solid var(--bf-line);
      border-radius: var(--bf-r-lg);
      background: var(--bf-white);
      font-family: var(--bf-font-display);
      font-size: var(--bf-fs-sm);
      font-weight: var(--bf-fw-semibold);
    }

    &__reference-label {
      color: var(--bf-fg-2);
    }

    &__reference-value {
      color: var(--bf-orange-dark);
      font-variant-numeric: tabular-nums;
      text-decoration: none;
    }

    a#{&}__reference-value:hover {
      text-decoration: underline;
      text-underline-offset: 3px;
    }

    &__actions {
      display: flex;
      flex-wrap: wrap;
      justify-content: center;
      gap: var(--bf-sp-2);
      margin-top: var(--bf-sp-4);
    }

    &__footer {
      width: 100%;
      margin-top: var(--bf-sp-4);
      padding-top: var(--bf-sp-3);
      border-top: 1px solid var(--bf-line);
      font-size: var(--bf-fs-sm);
      color: var(--bf-fg-2);

      &:empty {
        padding-top: 0;
      }
    }
  }

  @keyframes confirmation-panel-mark-in {
    from {
      transform: scale(0.6);
      opacity: 0;
    }

    to {
      transform: none;
      opacity: 1;
    }
  }

  @keyframes confirmation-panel-ring {
    from {
      transform: scale(0.85);
      opacity: 0.6;
    }

    to {
      transform: scale(1.4);
      opacity: 0;
    }
  }

  @media (max-width: 600px) {
    /* --bf-bp-sm */
    .confirmation-panel {
      padding-block: var(--bf-sp-3) var(--bf-sp-4);
    }

    .confirmation-panel__title {
      font-size: var(--bf-fs-xl);
    }

    .confirmation-panel__actions {
      flex-direction: column;
      align-items: stretch;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .confirmation-panel__mark,
    .confirmation-panel__mark::after {
      animation: none;
    }

    /* Dropped outright, not just stilled. */
    .confirmation-panel__mark::after {
      display: none;
    }
  }
</style>
